import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ListFilter, RotateCcw, Search } from 'lucide-react';
import FSelect from '@/components/ui/FSelect';
import { ALL_DISPOSITIONS } from '../mockData';
import { useReport } from '../ReportContext';
import { BUCKETS, WORK_BUCKETS } from '../theme';
import type { Filters } from '../types';

interface Props {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
  /** The download menu sits with the other controls. */
  actions?: React.ReactNode;
}

const field = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
const label = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-600';

/** The report builds each view live, so a range is capped (the server enforces the same limit). */
export const MAX_RANGE_DAYS = 92;
const DAY = 86400000;
function clampRange(p: { from?: string; to?: string }, f: Filters): Partial<Filters> {
  const from = p.from ?? f.from, to = p.to ?? f.to;
  const span = (new Date(to).getTime() - new Date(from).getTime()) / DAY;
  if (!(span > MAX_RANGE_DAYS)) return p;
  const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
  return p.from !== undefined ? { from, to: iso(new Date(from).getTime() + MAX_RANGE_DAYS * DAY) }
    : { to, from: iso(new Date(to).getTime() - MAX_RANGE_DAYS * DAY) };
}

const Select: React.FC<{
  title: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[];
}> = ({ title, value, onChange, options }) => (
  <div>
    <label className={label}>{title}</label>
    <select className={`${field} w-full`} value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

/** Date range stays in view; every other filter lives in one popover. Nothing was removed. */
const FilterBar: React.FC<Props> = ({ filters: f, onChange, onReset, actions }) => {
  const { agentOptions, catalog, mode } = useReport();
  const categories = catalog.map(c => c.category).sort((a, b) => a.localeCompare(b));
  const [open, setOpen] = useState(false);
  // Edits in the popover wait here; nothing reloads until Apply. Dates stay outside, they apply as you pick them.
  const [draft, setDraft] = useState<Filters>(f);
  const d = draft;
  const patchDraft = (p: Partial<Filters>) => setDraft(prev => ({ ...prev, ...p }));
  useEffect(() => { setDraft(prev => ({ ...f, from: prev.from, to: prev.to })); }, [f, open]);
  const POPOVER_KEYS = ['agent', 'buckets', 'worked', 'disposition', 'orderStatus', 'attribution', 'coupon', 'category', 'product', 'search'] as const;
  const dirty = POPOVER_KEYS.some(k => JSON.stringify(draft[k]) !== JSON.stringify(f[k]));
  const apply = () => {
    const patch: Partial<Filters> = {};
    POPOVER_KEYS.forEach(k => { (patch as Record<string, unknown>)[k] = draft[k]; });
    onChange(patch); setOpen(false);
  };
  const products = catalog.filter(c => draft.category === 'all' || c.category === draft.category).flatMap(c => c.products.map(p => p.product)).sort((a, b) => a.localeCompare(b));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const active = [f.agent !== 'all', f.buckets.length > 0, f.worked !== 'all', f.disposition !== 'all', f.orderStatus !== 'all',
    f.attribution !== 'all', f.coupon !== '', f.category !== 'all', f.product !== 'all', f.search !== ''].filter(Boolean).length;

  return (
    <div className="flex flex-wrap items-center gap-2" ref={ref}>
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
        <Calendar className="h-4 w-4 text-slate-600" />
        <input aria-label="From" type="date" className="bg-transparent outline-none" value={f.from} onChange={e => onChange(clampRange({ from: e.target.value }, f))} />
        <input aria-label="From time" title="From time (IST). Empty = start of day" type="time" className="bg-transparent outline-none" value={f.fromTime} onChange={e => onChange({ fromTime: e.target.value })} />
        <span className="text-slate-500">–</span>
        <input aria-label="To" type="date" className="bg-transparent outline-none" value={f.to} onChange={e => onChange(clampRange({ to: e.target.value }, f))} />
        <input aria-label="To time" title="To time (IST). Empty = end of day" type="time" className="bg-transparent outline-none" value={f.toTime} onChange={e => onChange({ toTime: e.target.value })} />
      </div>

      <div className="relative">
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="dialog"
          className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
          <ListFilter className="h-4 w-4" /> Filters
          {active > 0 && <span className="rounded-full bg-violet-100 px-1.5 text-xs font-semibold text-violet-800">{active}</span>}
        </button>
        {open && (
          <div role="dialog" aria-label="Filters"
            className="absolute right-0 z-30 mt-2 w-[min(44rem,92vw)] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-800">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Select title="Agent" value={d.agent} onChange={v => patchDraft({ agent: v })}
                options={[{ value: 'all', label: 'All agents' }, ...agentOptions.map(a => ({ value: a, label: a }))]} />
              <div>
                <label className={label}>
                  Bucket
                  {d.buckets.length > 0 && <span className="ml-2 rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold normal-case text-violet-800">{d.buckets.length}</span>}
                </label>
                <FSelect
                  options={WORK_BUCKETS.map(b => ({ opt_id: b, opt_name: BUCKETS[b].label }))}
                  value={d.buckets}
                  onChange={v => patchDraft({ buckets: v as Filters['buckets'] })}
                  placeholder="All buckets" searchable={false} className="w-full" />
              </div>
              <Select title="Worked" value={d.worked} onChange={v => patchDraft({ worked: v as Filters['worked'] })}
                options={[{ value: 'all', label: 'Worked + unworked' }, { value: 'worked', label: 'Worked' }, { value: 'unworked', label: 'Unworked' }]} />
              <Select title="Disposition" value={d.disposition} onChange={v => patchDraft({ disposition: v })}
                options={[{ value: 'all', label: 'Any disposition' }, ...ALL_DISPOSITIONS.map(d => ({ value: d, label: d }))]} />
              <Select title="Order status" value={d.orderStatus} onChange={v => patchDraft({ orderStatus: v as Filters['orderStatus'] })}
                options={[{ value: 'all', label: 'All orders' }, { value: 'valid', label: 'Valid' }, { value: 'cancelled', label: 'Cancelled' }]} />
              <Select title="Attribution" value={d.attribution} onChange={v => patchDraft({ attribution: v as Filters['attribution'] })}
                options={[{ value: 'all', label: 'Any source' }, { value: 'agent_tag', label: 'Agent Tag' }, { value: 'coupon', label: 'Coupon' }, { value: 'unattributed', label: 'Unattributed' }]} />
              <Select title="Category" value={d.category} onChange={v => patchDraft({ category: v, product: 'all' })}
                options={[{ value: 'all', label: 'All categories' }, ...categories.map(c => ({ value: c, label: c }))]} />
              <Select title="Product" value={d.product} onChange={v => patchDraft({ product: v })}
                options={[{ value: 'all', label: 'All products' }, ...products.map(p => ({ value: p, label: p }))]} />
              <div>
                <label className={label}>Agent coupon code</label>
                <input className={`${field} w-full`} placeholder="e.g. NANO5" value={d.coupon} onChange={e => patchDraft({ coupon: e.target.value })} />
              </div>
              <div className="col-span-2 md:col-span-3">
                <label className={label}>Phone / Order ID / AWB</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-slate-600" />
                  <input className={`${field} w-full pl-8`} placeholder="e.g. 98••••1234 or #ZH633616"
                    value={d.search} onChange={e => patchDraft({ search: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-slate-700">
              <p className="text-[11px] text-slate-600">
                {mode === 'live'
                  ? 'Date range, Agent and Bucket change every number. Category and Product change the Category › Product › Agent tab. Worked and Order status apply to the records behind a number and to downloads. Disposition, Attribution, Coupon and Search are not applied yet.'
                  : 'Sample data: Agent and Bucket change the numbers on every tab; Category and Product change the Category › Product › Agent tab. The others only show where they will sit.'}
              </p>
              <div className="flex shrink-0 items-center gap-2">
                <button type="button" onClick={() => { onReset(); setOpen(false); }}
                  className="flex items-center gap-1 rounded-lg px-2.5 py-1 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700">
                  <RotateCcw className="h-4 w-4" /> Reset
                </button>
                <button type="button" onClick={apply} disabled={!dirty}
                  className="rounded-lg bg-brandColor px-4 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40">
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {actions}
    </div>
  );
};

export default FilterBar;
